import React, { FunctionComponent, useEffect, useState } from 'react';
import { useMedia } from 'react-media-match';
import styled from 'styled-components';
import { useHistory } from 'react-router';
import { Image } from '@empiriecom/module-components/Image';
import Price from '@empiriecom/module-components/Price';
import useDebounce from '@empiriecom/module-components/hooks/useDebounce';
import { event } from '@empiriecom/module-components/EventBridge';
import CompactDetailViewSkeleton from './Skeleton';
import { AvailabilityMessage } from '../Delivery/AvailabilityMessage';
import { CompactHeadline, MadeToMeasure, ProductButton } from './components';
import { TaxHint } from '../TaxHint';
import { PickerTiles } from '../ProductSelection/PickerTiles';
import CollapsibleContextProvider from '../Card/CollapsibleContext';
import { useMadeToMeasure, useProduct } from '../ProductContext';
import setVariation from '../PageContainer/setVariation';
import usePage from '../ProductContext/hooks/usePage';
import PowerEfficiency from '../PowerEfficiency';

const CompactDetailViewWrapper = styled.div<{ mobile: boolean }>`
  margin: ${({ mobile }) => (mobile ? '0.5rem auto 0;' : '1.938rem auto 0;')};
  ${({ mobile }) => !mobile && 'padding: 0 1.75rem;'};
  width: 100%;
  max-width: 48rem;
  overflow: hidden;
  ${({ theme, mobile }) => mobile && `background-color: ${theme.greyLight.color.normal};`};
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-flow: ${({ mobile }) => (mobile ? 'row wrap;' : 'row nowrap;')};
`;
const ProductDetailsWrapper = styled.div<{ mobile: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-flow: row wrap;
  width: ${({ mobile }) => (mobile ? '100%;' : '22.188rem;')};
  ${({ mobile }) => !mobile && 'padding-right:1.25rem;'};
`;

const DimensionsWrapper = styled.div<{ mobile: boolean }>`
  ${({ mobile, theme }) => !mobile && `border-left: 1px solid ${theme.greyNormal.color.normal}`};
  ${({ mobile }) => !mobile && 'padding-left:1rem;'};
  height: 100%;
  overflow-y: auto;
`;

const ProductDetails = styled.div<{ mobile: boolean }>`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: ${({ mobile }) => (mobile ? 'row' : 'column')};
  ${({ mobile }) => mobile && 'background-color: #fff;'};
  ${({ mobile }) => mobile && 'padding: 0 0.75rem;'};
`;

const ProductImageWrapper = styled.div<{ mobile: boolean }>`
  position: relative;
  width: ${({ mobile }) => (mobile ? '6.25rem' : '20.938rem')};
  height: ${({ mobile }) => (mobile ? '6.25rem' : '20.938rem')};
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const ImageAndEECWrapper = styled.div<{ mobile: boolean }>`
  width: ${({ mobile }) => (mobile ? '6.25rem' : '20.938rem')};
  margin-right: ${({ mobile }) => (mobile ? '1.5rem' : '1.25rem')};
`;

const ProductLinkWrapper = styled.div`
  width: 100%;
  padding: 0.65rem 1rem 0.75rem;
  background-color: #fff;
`;

/**
 * This component renders the compact detail view.
 */
export const CompactDetailView: FunctionComponent = (): JSX.Element => {
  const [selectedProductId, setSelectedProductId] = useState<string>();
  const { error, isLoading, product } = useProduct();
  const { length, width, engraving } = useMadeToMeasure();
  const [widthValue, setWidthValue] = useState<number>(length);
  const [lengthValue, setLengthValue] = useState<number>(width);
  const [engravingValue, setEngravingValue] = useState<string>(engraving);
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const isMadeToMeasure = product?.dimensions?.some(
    (dimension) => dimension.axisType === 'LENGTH' || dimension.axisType === 'WIDTH',
  );
  const renderMadeToMeasure = isMadeToMeasure || product?.attributes?.engraving;
  const productLoading = useDebounce(isLoading, 300);
  const debouncedWidth = useDebounce(widthValue, 500);
  const debouncedLength = useDebounce(lengthValue, 500);
  const debouncedEngraving = useDebounce(engravingValue, 500);
  const history = useHistory();
  const { isBot } = usePage();

  useEffect(() => {
    if (
      !productLoading &&
      (selectedProductId !== '' ||
        debouncedWidth !== 0 ||
        debouncedLength !== 0 ||
        debouncedEngraving !== '')
    ) {
      const compactDetailviewData = {
        engraving: debouncedEngraving !== '' ? debouncedEngraving : null,
        lengthInCentimeters: debouncedLength !== 0 ? debouncedLength : null,
        productId: selectedProductId || product?.sku,
        widthInCentimeters: debouncedWidth !== 0 ? debouncedWidth : null,
      };
      event.trigger('compactDetailviewChange', compactDetailviewData);
    }
  }, [selectedProductId, debouncedWidth, debouncedLength, debouncedEngraving]);

  return (
    <>
      {error && <p>Error: {error.description}</p>}
      {productLoading && <CompactDetailViewSkeleton />}
      {product && (
        <CollapsibleContextProvider>
          <CompactDetailViewWrapper mobile={isMobile} data-testid="compact-detail-view-wrapper">
            <ProductDetailsWrapper mobile={isMobile} data-testid="compact-product-details-wrapper">
              <ProductDetails mobile={isMobile} data-testid="compact-product-details">
                <ImageAndEECWrapper mobile={isMobile}>
                  <ProductImageWrapper mobile={isMobile} data-testid="compact-product-image">
                    <Image
                      alt={product?.name}
                      src={product?.images[0].concat('?$format_dv_mob_075$')}
                      fallback={() => null}
                    />
                  </ProductImageWrapper>
                </ImageAndEECWrapper>
                <div data-testid="compact-product-information">
                  <CompactHeadline mobile={isMobile} name={product?.name} />
                  <AvailabilityMessage
                    message={product?.availability?.message}
                    state={product?.availability?.state}
                    layout="small"
                  />
                  <Price
                    amount={product?.price.amount && product?.price.amount}
                    currency={product?.price.currency}
                    isMSRP={product?.price.uvp && product?.price.uvp}
                    isStartPrice={product?.price.isStartPrice && product?.price.isStartPrice}
                    layout={isMobile ? 'defaultInCard' : 'default'}
                    oldPrice={product?.price.valueOld && product?.price.valueOld}
                    price={product?.price.value}
                    saving={product?.price.saving && product?.price.saving.value}
                    savingPrefix
                    savingType={
                      product?.price.saving && product?.price.saving.type
                        ? product?.price.saving.type
                        : 'CURRENCY'
                    }
                    unit={product?.price.unit && product?.price.unit}
                    unitPrice={product?.price.valueUnit && product?.price.valueUnit}
                  />
                  <TaxHint deliveryType={product?.deliveryType} isModal={false} />
                  {product?.attributes && product?.attributes?.powerefficiency && (
                    <PowerEfficiency
                      attribute={product?.attributes.powerefficiency}
                      layout="compact"
                    />
                  )}
                </div>
              </ProductDetails>
              <ProductLinkWrapper data-testid="compact-product-link-wrapper">
                <ProductButton
                  productId={selectedProductId || product?.sku}
                  seoSlug={product?.slug}
                />
              </ProductLinkWrapper>
            </ProductDetailsWrapper>
            <DimensionsWrapper mobile={isMobile}>
              <PickerTiles
                product={product}
                setProduct={(prodId: string) => {
                  setVariation(prodId, history, isBot);
                  setSelectedProductId(prodId);
                }}
              />
              {renderMadeToMeasure && (
                <MadeToMeasure
                  isMadeToMeasure={isMadeToMeasure}
                  product={product}
                  setEngravingValue={setEngravingValue}
                  setLengthValue={setLengthValue}
                  setWidthValue={setWidthValue}
                />
              )}
            </DimensionsWrapper>
          </CompactDetailViewWrapper>
        </CollapsibleContextProvider>
      )}
    </>
  );
};

export default CompactDetailView;
